/**
 * ヘッダードロップダウンメニュー
 *
 * PC: ホバーでドロップダウンが開く
 * SP: タップでドロップダウンが開く（ドロワー形式で表示）
 *
 * 使い方:
 * HTML:
 * <li class="js-header-dropdown" data-dropdown="order-guide">
 *   <button type="button" class="m-header__nav-link" aria-expanded="false" aria-haspopup="true">
 *     メニュー名
 *   </button>
 * </li>
 * <div class="m-header__dropdown" id="dropdown-order-guide" aria-hidden="true">
 *   ...
 * </div>
 */
export const headerDropdown = () => {
  const triggers = document.querySelectorAll('.js-header-dropdown');
  const dropdowns = document.querySelectorAll('.m-header__dropdown');
  const header = document.getElementById('js-header');

  if (triggers.length === 0 || dropdowns.length === 0) return;

  let activeDropdown = null;
  let closeTimeout = null;

  // ブレークポイント（md: 768px）
  const BREAKPOINT_MD = 768;

  /**
   * SP表示かどうかを判定
   * @returns {boolean}
   */
  const isMobile = () => window.innerWidth < BREAKPOINT_MD;

  /**
   * ドロップダウンを開く
   * @param {HTMLElement} trigger - トリガー要素
   * @param {HTMLElement} dropdown - ドロップダウン要素
   */
  const openDropdown = (trigger, dropdown) => {
    // タイムアウトをクリア
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      closeTimeout = null;
    }

    // 他のドロップダウンを閉じる
    if (activeDropdown && activeDropdown !== dropdown) {
      closeDropdown(activeDropdown);
    }

    // ドロップダウンを開く
    trigger.classList.add('is-active');
    trigger.querySelector('button')?.setAttribute('aria-expanded', 'true');
    dropdown.classList.add('is-visible');
    dropdown.setAttribute('aria-hidden', 'false');
    activeDropdown = dropdown;
  };

  /**
   * ドロップダウンを閉じる
   * @param {HTMLElement} dropdown - ドロップダウン要素
   */
  const closeDropdown = (dropdown) => {
    if (!dropdown) return;

    const dropdownId = dropdown.id.replace('dropdown-', '');
    const trigger = document.querySelector(
      `.js-header-dropdown[data-dropdown="${dropdownId}"]`,
    );

    trigger?.classList.remove('is-active');
    trigger?.querySelector('button')?.setAttribute('aria-expanded', 'false');
    dropdown.classList.remove('is-visible');
    dropdown.setAttribute('aria-hidden', 'true');

    if (activeDropdown === dropdown) {
      activeDropdown = null;
    }
  };

  /**
   * 全てのドロップダウンを閉じる
   */
  const closeAllDropdowns = () => {
    dropdowns.forEach((dropdown) => {
      closeDropdown(dropdown);
    });
    activeDropdown = null;
  };

  /**
   * PC: 遅延付きで閉じる
   */
  const closeWithDelay = () => {
    if (isMobile()) return;

    closeTimeout = setTimeout(() => {
      closeAllDropdowns();
    }, 150);
  };

  // トリガーのイベント設定
  triggers.forEach((trigger) => {
    const dropdownId = trigger.dataset.dropdown;
    const dropdown = document.getElementById(`dropdown-${dropdownId}`);
    const button = trigger.querySelector('button');

    if (!dropdown || !button) return;

    // PC: マウスホバー時に開く
    trigger.addEventListener('mouseenter', () => {
      if (!isMobile()) {
        openDropdown(trigger, dropdown);
      }
    });

    // PC: マウスが離れたら遅延付きで閉じる
    trigger.addEventListener('mouseleave', () => {
      if (!isMobile()) {
        closeWithDelay();
      }
    });

    // クリック/タッチでトグル
    button.addEventListener('click', (e) => {
      e.preventDefault();
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        closeDropdown(dropdown);
      } else {
        openDropdown(trigger, dropdown);
      }
    });

    // キーボード操作
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDropdown(dropdown);
        button.focus();
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const firstLink = dropdown.querySelector('a');
        firstLink?.focus();
      }
    });
  });

  // ドロップダウンのイベント設定
  dropdowns.forEach((dropdown) => {
    // PC: マウスがドロップダウン内に入ったらタイムアウトをクリア
    dropdown.addEventListener('mouseenter', () => {
      if (!isMobile() && closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
    });

    // PC: マウスがドロップダウンから離れたら遅延付きで閉じる
    dropdown.addEventListener('mouseleave', () => {
      if (!isMobile()) {
        closeWithDelay();
      }
    });

    // ドロップダウン内のキーボード操作
    dropdown.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDropdown(dropdown);
        const dropdownId = dropdown.id.replace('dropdown-', '');
        const trigger = document.querySelector(
          `.js-header-dropdown[data-dropdown="${dropdownId}"] button`,
        );
        trigger?.focus();
      }
    });
  });

  // ヘッダー外をクリックしたら閉じる（PC）
  document.addEventListener('click', (e) => {
    if (!isMobile() && !header?.contains(e.target)) {
      closeAllDropdowns();
    }
  });

  // スクロール時に閉じる（PC）
  let scrollTimeout = null;
  window.addEventListener(
    'scroll',
    () => {
      if (!isMobile()) {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        scrollTimeout = setTimeout(() => {
          closeAllDropdowns();
        }, 100);
      }
    },
    { passive: true },
  );

  // リサイズ時に閉じる
  window.addEventListener('resize', () => {
    closeAllDropdowns();
  });
};
